import { CITY } from '@city'
import { LegalLayout } from '../components/LegalLayout'

/**
 * Terms and Conditions for london-tube.com (web) and the Tube Map &
 * London Underground mobile app (iOS + Android). Ported from the
 * legacy mmthstudio.com/tube/terms-and-conditions and corrected for
 * the actual third-party stack the Metro product depends on (GA4,
 * Amplitude, AppsFlyer, Google Maps, Adapty).
 *
 * Linked from:
 *   - Footer of every page on london-tube.com
 *   - Privacy Policy page
 */
export function TermsAndConditionsPage() {
  return (
    <LegalLayout current="terms">
      <h1>Terms and Conditions</h1>

      <p>
        These terms and conditions apply to <strong>{CITY.domain}</strong> (the "Service") and the{' '}
        <strong>{CITY.serviceName}</strong> mobile app (the "Application") for iOS and Android, both
        operated by <strong>Individual Entrepreneur Pavel Kuzin</strong> ("Service Provider") as a
        Freemium service.
      </p>

      <p>
        By downloading, installing, or otherwise using the Application or the Service, you agree to
        these terms. Please read them carefully before continuing.
      </p>

      <h2>Intellectual property</h2>
      <p>
        Unauthorised copying, modification, reverse engineering, or translation of the Application,
        any part of it, or our trademarks is prohibited. All trademarks, copyrights, database
        rights, and other intellectual property rights related to the Application and the Service
        remain the property of the Service Provider.
      </p>

      <h2>Modifications and pricing</h2>
      <p>
        The Service Provider strives to keep the Application useful and efficient. The Service
        Provider reserves the right to modify the Application or charge for any feature at any time.
        Any charges will be communicated clearly before they apply, and active subscriptions are
        governed by Apple App Store or Google Play billing terms in addition to these terms.
      </p>

      <h2>Personal data and device security</h2>
      <p>
        The Application and the Service process personal data you have provided in order to deliver
        the Service. You are responsible for maintaining the security of your phone and access to
        the Application. Jailbreaking or rooting your device is strongly discouraged — it may expose
        your phone to malware, compromise security features, and may cause the Application to
        malfunction or stop working entirely.
      </p>

      <h2>Third-party services</h2>
      <p>
        The Application and the Service rely on third-party services that have their own terms. By
        using the Service you accept those terms in addition to these:
      </p>
      <ul>
        {CITY.webAnalytics && (
          <>
            <li>
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Google Analytics — Terms of Service
              </a>
            </li>
          </>
        )}
        <li>
          <a href="https://amplitude.com/terms" target="_blank" rel="noopener noreferrer">
            Amplitude — Terms of Service
          </a>
        </li>
        <li>
          <a
            href="https://www.appsflyer.com/legal/terms-of-use/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AppsFlyer — Terms of Use
          </a>
        </li>
        <li>
          <a
            href="https://cloud.google.com/maps-platform/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Maps Platform — Terms of Service
          </a>
        </li>
        <li>
          <a href="https://adapty.io/terms" target="_blank" rel="noopener noreferrer">
            Adapty — Terms of Service
          </a>
        </li>
      </ul>

      <h2>Connectivity and data charges</h2>
      <p>
        Some features of the Application require an active internet connection (Wi-Fi or mobile
        data). The Service Provider cannot be held responsible if the Application does not function
        at full capacity due to lack of Wi-Fi access or because you have exhausted your mobile data
        allowance. The {CITY.mapName}, journey planner, and station details work fully offline once
        the Application has been opened with internet access at least once.
      </p>

      <p>
        If you use the Application outside a Wi-Fi area, your mobile network provider's agreement
        still applies. You may incur charges from your mobile provider for data usage and roaming.
        By using the Application you accept responsibility for those charges. If you are not the
        bill payer for the device, the Service Provider assumes you have the bill payer's
        permission.
      </p>

      <h2>Device responsibility</h2>
      <p>
        You are responsible for ensuring your device remains charged and operational. If your device
        runs out of battery and you cannot access the Service, the Service Provider cannot be held
        responsible.
      </p>

      <h2>Accuracy of information</h2>
      <p>
        The Service Provider strives to keep the Application updated and accurate, but relies on
        third-party data sources (e.g. {CITY.dataOperator} open data) for some content. The Service
        Provider accepts no liability for any loss, direct or indirect, you experience as a result
        of relying entirely on the Application's information for travel decisions. Always check
        official {CITY.dataOperator} channels for live service status before time-critical journeys.
      </p>

      <h2>Updates and termination</h2>
      <p>
        The Service Provider may update the Application from time to time. Operating-system
        requirements may change, and you may need to install updates to continue using the
        Application. The Service Provider does not guarantee continued compatibility with every
        device or OS version. The Service Provider may also cease offering the Application or the
        Service at any time. Upon termination, all rights and licences granted to you end and you
        must stop using and (where appropriate) delete the Application from your device.
      </p>

      <h2>Auto-renewable subscriptions</h2>
      <p>
        <strong>Custom Premium</strong> is offered as an auto-renewable subscription. The available
        plans, their duration, and prices are shown in the Application before you confirm a
        purchase. Payment is charged to your Apple ID (or Google Play account) at confirmation of
        purchase.
      </p>
      <p>
        The subscription automatically renews for the same period unless auto-renew is turned off at
        least 24 hours before the end of the current period. Your account is charged for renewal
        within 24 hours before the current period ends, at the price of the selected plan. You can
        manage or cancel your subscription at any time in your App Store account settings (or your
        Google Play subscriptions). Where a free trial is offered, any unused portion of the trial
        is forfeited when you purchase a subscription to the same content, where permitted by law.
      </p>

      <h2>Apple App Store</h2>
      <p>
        The following additional terms apply when you obtain the Application from the Apple App
        Store:
      </p>
      <ul>
        <li>
          This agreement is concluded between you and the Service Provider only, and not with Apple.
          The Service Provider, not Apple, is solely responsible for the Application and its
          content.
        </li>
        <li>
          Your licence to use the Application is a non-transferable licence to use it on any
          Apple-branded device you own or control, as permitted by the Usage Rules in the Apple
          Media Services Terms and Conditions.
        </li>
        <li>
          Apple has no obligation to furnish any maintenance or support services for the
          Application.
        </li>
        <li>
          If the Application fails to conform to any applicable warranty, you may notify Apple and
          Apple will refund the purchase price (if any). To the maximum extent permitted by law,
          Apple has no other warranty obligation with respect to the Application; any other claims,
          losses, liabilities, damages, costs, or expenses attributable to a failure to conform to a
          warranty are the Service Provider's responsibility.
        </li>
        <li>
          The Service Provider, not Apple, is responsible for addressing any claims by you or a
          third party relating to the Application, including product-liability claims, claims that
          the Application fails to meet a legal or regulatory requirement, claims under
          consumer-protection or privacy law, and intellectual-property infringement claims.
        </li>
        <li>
          You represent that you are not located in a country subject to a U.S. Government embargo
          or designated as a "terrorist supporting" country, and that you are not listed on any U.S.
          Government list of prohibited or restricted parties.
        </li>
        <li>
          Apple and its subsidiaries are third-party beneficiaries of this agreement and, upon your
          acceptance, have the right (and are deemed to have accepted the right) to enforce this
          agreement against you.
        </li>
        <li>
          For any questions, complaints, or claims regarding the Application, contact the Service
          Provider at <a href="mailto:support@mmthstudio.com">support@mmthstudio.com</a>.
        </li>
      </ul>

      <h2>Changes to These Terms and Conditions</h2>
      <p>
        The Service Provider may periodically update these Terms and Conditions. You are advised to
        review this page regularly. The Service Provider will notify you of material changes by
        posting the revised Terms and Conditions on this page.
      </p>

      <h2>Contact</h2>
      <p>
        If you have questions or suggestions about these Terms and Conditions, contact the Service
        Provider at <a href="mailto:support@mmthstudio.com">support@mmthstudio.com</a>.
      </p>
    </LegalLayout>
  )
}
